import StateObserver, { user } from '../../StateObserver';
import {
  mapFeatureToDialogOrigin,
  openPopupPromise,
} from '../../lib/popups/popupOpenClose';
import { GCInstant as platform } from 'src/lib/gcinstant';
import * as Social from '../../replicant/social';
import { shuffleArray } from 'src/replicant/utils/random';
import { isPlatformGuest } from '../../lib/utils';
import { getTutorialController } from '../tutorial/TutorialController';
import { SocialSubFeature } from 'src/replicant/social/social.ruleset';
import { Feature } from 'src/replicant/analytics';
import { getRandomShareInviteCreative } from 'src/creatives/share';
import { inviteAsync } from 'src/lib/sharing';
import { getCreativeText } from 'src/creatives/text';
import { trackPinchConversion } from 'src/lib/analytics/events';
import features from 'src/replicant/ruleset/features';
import { PinchType } from '../shop/Helpers';
import { canAttemptPayments } from 'src/lib/iap/iap.util';
import { getSocialFriendsList } from './social.utils';
import { isDynamicTestInBucket } from 'src/replicant/getters/ab';
import ab from 'src/replicant/ruleset/ab';
import * as Tutorial from 'src/replicant/tutorial';
import { track } from 'src/lib/analytics/utils';
import { promptLINELoginNew } from '../guest/guest.sequence';
import { isGuestUser } from 'src/replicant/guest/guest.getters';

export type SocialFriendProfile = Pick<
  (typeof platform)['socialFriends'][number],
  'id' | 'name' | 'photo'
>;

/**
 * Start invite loop sequence return invited friends
 *
 * Use this function when you want to show invite popup
 * Do not show invite popup directly use only this function!
 *
 * @param feature Analytics feature that opened the invite flow.
 * @param subFeature Analytics subfeature that opened the invite flow.
 * @param pinch Optional resource pinch that triggered the invite flow.
 * @param inviteRequirement Optional number of invites required by the caller.
 * @returns Invited friends when known, an empty array after direct sharing, or
 * null when no invite action was taken.
 */
export async function startInviteFriendsSequence(
  feature: string,
  subFeature: SocialSubFeature,
  pinch?: PinchType,
  inviteRequirement?: number,
): Promise<SocialFriendProfile[] | null> {
  const origin = mapFeatureToDialogOrigin(subFeature);
  const popupId = 'popupGenericInviteFriends';

  // Show demo mode popup, we stop game here
  // If we are in demo mode and the user has completed the first puzzle, we show the LINE login popup
  // TEST_DEMO_MODE
  if (
    features(user()).isDemoMode &&
    isDynamicTestInBucket(user(), ab.tests.TEST_DEMO_MODE, 'afterTutorial')
  ) {
    await openPopupPromise('popupLineDemoLogin', { origin });
    return null;
  }

  // Show guest mode pinch.
  if (isPlatformGuest(platform)) {
    await promptLINELoginNew('inviteSequence');

    return null;
  }

  // Bail if feature is not available.
  if (!features(user()).friendReferral) return null;

  // Show tutorial if not seen.
  await getTutorialController().handleEvent('friendReferralPopupOpened');

  // Track InviteOpen event
  track('InviteOpen', {
    popupId,
    origin,
  });

  const didInvite = await openPopupPromise(popupId, {
    origin,
  });

  // Direct sharing cannot identify recipients, so use an empty successful list.
  if (didInvite) return [];

  // Closing the popup without sharing does not count as an invite action.
  return null;
}

/**
 * One iteration of invite sequence
 *
 * 1. Show friend selection
 * 2. Show confirmation for all sections
 * 3. Send invite message
 * 4. Return invited friends
 *
 * @return SocialFriendProfile[] - Invited friends
 * @return null - Cancel selection dialog
 * @return empty array - Canceled on confirmation phase
 */
async function inviteFriendsSequence(
  friends: SocialFriendProfile[],
  feature: string,
  subFeature: SocialSubFeature,
  pinch?: PinchType,
): Promise<SocialFriendProfile[] | null> {
  const origin = mapFeatureToDialogOrigin(subFeature);

  // Open invite popup with pre-processed friends list
  const selected = await openPopupPromise('popupInviteFriends', {
    friends: friends,
    subFeature,
    pinch,
    origin,
    additionalTracking: {
      originFeature: subFeature,
      pinch: pinch,
    },
  });

  // User haven't select anyone
  if (!selected || selected.length === 0) {
    return null;
  }

  // If have been selected more then one user we show popupConfirmInviteMulti
  // If was selected one user we will show popupConfirmInvite
  if (selected.length > 1) {
    const avatars = selected.map((x) => x.photo);
    const ids = selected.map((x) => x.id);
    if (
      await openPopupPromise('popupConfirmInviteMulti', { avatars, origin })
    ) {
      await inviteFriends(ids, feature, subFeature);
      return selected;
    }
  } else {
    const friend = selected[0];
    const friendID = friend.id;
    if (await openPopupPromise('popupConfirmInvite', { ...friend, origin })) {
      await inviteFriends([friendID], feature, subFeature);
      return selected;
    }
  }

  // Player rejected confirmation
  return [];
}

/**
 * Send invitation to friend
 *
 * @param friendIds
 */
async function inviteFriends(
  friendIds: string[],
  feature: string,
  subFeature: SocialSubFeature,
) {
  const wasInviteChallengeComplete =
    Social.getters.isInviteChallengeComplete(user());

  await inviteControl(friendIds, feature, subFeature);

  const isInviteChallengeComplete =
    Social.getters.isInviteChallengeComplete(user());

  if (isInviteChallengeComplete && !wasInviteChallengeComplete) {
    track('CampaignMissionComplete', {
      campaignName: 'wallpaperPresent',
      missionName: 'invites_10',
    });

    await openPopupPromise('popupInviteChallengeComplete', {
      origin: mapFeatureToDialogOrigin(subFeature),
    });
  }
}

/**
 * TODO: Remove — sendInvitesControl always returns [] after PlatformLine removal,
 * so the reward block below is unreachable. Part of the dead inviteAsync flow.
 */
async function inviteControl(
  friendIds: string[],
  feature: string,
  subFeature: SocialSubFeature,
) {
  const receiverIDs = await sendInvitesControl(friendIds, feature, subFeature);

  if (receiverIDs?.length) {
    await openPopupPromise('popupInviteReward', {
      inviteCount: receiverIDs.length,
      subFeature: subFeature,
      origin: mapFeatureToDialogOrigin(subFeature),
    });

    const reward = await StateObserver.invoke.claimInviteRewards(subFeature);

    trackPinchConversion({
      action: 'viral',
      feature: Feature.INVITE._,
      subFeature: subFeature,
      resource: reward.type,
    });
  }
}

export async function sendInvitesControl(
  friendIds: string[],
  feature: string,
  subFeature: SocialSubFeature,
  extraProperties?: Record<string, unknown>,
): Promise<string[]> {
  try {
    const creativeAsset = getRandomShareInviteCreative();

    return await inviteAsync({
      receiverIDs: friendIds,
      confirmPopup: false,
      creativeAsset: await creativeAsset,
      creativeText: getCreativeText('invite'),
      data: {
        feature,
        $subFeature: subFeature,
        ...extraProperties,
      },
    });
  } catch (e: any) {
    // USER_INPUT error handled by platform
    if (e.code === 'USER_INPUT') {
      // Line throws USER_INPUT, dont show it again
    }
  }

  return [];
}

export async function getFriendsForInvite() {
  // Invite-able friends.
  const { affinity, nonAffinity } = await getSocialFriendsList();

  // Create dictionary of excluded friends for invite
  const { getExcludedFriendsForInvite } = Social.getters;
  const excludedFriends = getExcludedFriendsForInvite(
    user(),
    [...affinity, ...nonAffinity].map(({ id }) => id),
    StateObserver.now(),
    canAttemptPayments(user()),
  ).reduce(
    (acc, id) => ({
      ...acc,
      [id]: true,
    }),
    {} as Record<string, boolean>,
  );

  // Filter and sort friends
  const filterExcludedFriend = ({ id }: SocialFriendProfile) =>
    !excludedFriends[id];
  const affinityFiltered = affinity.filter(filterExcludedFriend);
  const nonAffinityFiltered = nonAffinity.filter(filterExcludedFriend);
  shuffleArray(nonAffinityFiltered, Math.random);

  // Friends available for invites
  return [...affinityFiltered, ...nonAffinityFiltered];
}

/**
 * Show friend invite popup when player unlocks it.
 * @returns - True if flow is triggered.
 */
export async function inviteFriendUnlockFlow() {
  const { user } = StateObserver.getState();

  // Skip for guest mode.
  if (isGuestUser(user) && isPlatformGuest(platform)) {
    return false;
  }

  // Tutorial already completed, bail.
  if (Tutorial.getters.isTutorialStepComplete(user, 'friendReferral')) {
    return false;
  }

  // Feature not unlocked, bail.
  const { minigamesToUnlockInvite: requiredMinigames } = Social.ruleset;
  // Match2 level minus 1 is the number of puzzles user has played so far.
  if (user.match2.level - 1 < requiredMinigames) {
    return false;
  }

  // Trigger invite sequence.
  await startInviteFriendsSequence(
    Feature.INVITE._,
    Feature.INVITE.INVITE_UNLOCK,
  );

  return true;
}
